import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchConversations, fetchConversation, updateConversationStatus } from '../api';
import { ConversationDetail, ConversationSummary } from '../types';
import { useAdminLang } from '../App';
import { ADMIN_LABELS } from '../i18n';
import MapView from '../components/MapView';
import ConversationModal from '../components/ConversationModal';
import './ConversationsPage.scss';

// ── Category definitions ──────────────────────────────────────────────────────
type CategoryKey = 'all' | 'police' | 'ambulance' | 'civil_defense' | 'inquiry' | 'callback';

interface Category {
  key: CategoryKey;
  labelKey: keyof typeof ADMIN_LABELS['en'];
}

const CATEGORIES: Category[] = [
  { key: 'police',        labelKey: 'catPolice'      },
  { key: 'ambulance',     labelKey: 'catAmbulance'   },
  { key: 'civil_defense', labelKey: 'catCivilDefense'},
  { key: 'inquiry',       labelKey: 'catInquiry'     },
  { key: 'callback',      labelKey: 'catCallBack'    },
];

function getConversationCategories(conv: ConversationSummary): CategoryKey[] {
  const cats: CategoryKey[] = [];
  const agencies = conv.agencies || [];
  if (agencies.includes('police')) cats.push('police');
  if (agencies.includes('ambulance')) cats.push('ambulance');
  if (agencies.includes('civil_defense')) cats.push('civil_defense');
  if (conv.human_requested) cats.push('callback');
  if (cats.length === 0) cats.push('inquiry');
  return cats;
}

function formatDate(ts: string | null | undefined): string {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    const month = d.toLocaleDateString(undefined, { month: 'short' });
    const day = d.getDate();
    const year = d.getFullYear();
    const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `${month} ${day}, ${year} ${time}`;
  } catch { return ts; }
}

function getDisplayName(conv: ConversationSummary): string {
  return conv.user_name || conv.user_id || 'Unknown';
}

// Status badge config
function getStatusBadges(conv: ConversationSummary) {
  const badges: { label: string; type: string }[] = [];

  // Always show a status badge — fallback to 'Open' for any unrecognised value
  if (conv.status === 'resolved') {
    badges.push({ label: 'Resolved', type: 'resolved' });
  } else if (conv.status === 'pending_human') {
    badges.push({ label: 'Pending', type: 'pending' });
  } else {
    // 'open' or any other / missing value → show as Open
    badges.push({ label: 'Open', type: 'open' });
  }

  const agencies = (conv.agencies || []) as string[];
  if (agencies.includes('police')) badges.push({ label: 'Police', type: 'police' });
  if (agencies.includes('ambulance')) badges.push({ label: 'Ambulance', type: 'ambulance' });
  if (agencies.includes('civil_defense')) badges.push({ label: 'Civil defence', type: 'civil' });
  if (agencies.includes('fire')) badges.push({ label: 'Fire', type: 'fire' });
  if (agencies.includes('emergency_services')) badges.push({ label: 'Emergency Services', type: 'emergency_services' });
  if (agencies.includes('coast_guard')) badges.push({ label: 'Coast Guard', type: 'coast_guard' });
  if (agencies.includes('rescue')) badges.push({ label: 'Rescue', type: 'rescue' });
  if (conv.human_requested) badges.push({ label: 'Call back', type: 'callback' });
  return badges;
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ConversationsPage() {
  const { lang } = useAdminLang();
  const t = ADMIN_LABELS[lang];

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<CategoryKey>('police');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modalConv, setModalConv] = useState<ConversationDetail | null>(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [mobileView, setMobileView] = useState<'list' | 'map' | 'detail'>('list');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── List panel drag-resize ─────────────────────────────────────────────────
  const [panelWidth, setPanelWidth] = useState(680);
  const isListDragging = useRef(false);
  const listDragStartX = useRef(0);
  const listDragStartW = useRef(0);

  // ── Detail panel drag-resize ───────────────────────────────────────────────
  const [detailWidth, setDetailWidth] = useState(480);
  const isDetailDragging = useRef(false);
  const detailDragStartX = useRef(0);
  const detailDragStartW = useRef(0);

  const onResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isListDragging.current = true;
    listDragStartX.current = e.clientX;
    listDragStartW.current = panelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [panelWidth]);

  const onDetailResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDetailDragging.current = true;
    detailDragStartX.current = e.clientX;
    detailDragStartW.current = detailWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [detailWidth]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (isListDragging.current) {
        const delta = e.clientX - listDragStartX.current;
        setPanelWidth(Math.min(800, Math.max(400, listDragStartW.current + delta)));
      }
      if (isDetailDragging.current) {
        const delta = e.clientX - detailDragStartX.current;
        setDetailWidth(Math.min(800, Math.max(320, detailDragStartW.current + delta)));
      }
    };
    const onMouseUp = () => {
      isListDragging.current = false;
      isDetailDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      const data = await fetchConversations();
      setConversations(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
    intervalRef.current = setInterval(loadConversations, 15000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [loadConversations]);

  const openModal = useCallback(async (conv: ConversationSummary) => {
    setSelectedId(conv.id);
    setLoadingModal(true);
    // On mobile, switch to dedicated detail view
    if (window.innerWidth <= 768) {
      setMobileView('detail');
    }
    try {
      const detail = await fetchConversation(conv.id);
      setModalConv(detail);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingModal(false);
    }
  }, []);

  const closeModal = useCallback(() => {
    setModalConv(null);
    setSelectedId(null);
    if (window.innerWidth <= 768) setMobileView('list');
  }, []);

  const handleResolve = async (id: string) => {
    try {
      await updateConversationStatus(id, 'resolved');
      setConversations(cs => cs.map(c => c.id === id ? { ...c, status: 'resolved' as const } : c));
      if (modalConv?.id === id) setModalConv(m => m ? { ...m, status: 'resolved' as const } : m);
    } catch (err) {
      console.error(err);
    }
  };

  // Filter by search
  const searched = conversations.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c.user_name || '').toLowerCase().includes(q) ||
      (c.user_phone || '').toLowerCase().includes(q) ||
      (c.user_id || '').toLowerCase().includes(q) ||
      (c.incident_number || '').toLowerCase().includes(q)
    );
  });

  // Count per category
  const counts = new Map<CategoryKey, number>();
  for (const cat of CATEGORIES) {
    counts.set(cat.key, searched.filter(c => getConversationCategories(c).includes(cat.key)).length);
  }

  // Filter by active tab
  const tabFiltered = searched.filter(c => getConversationCategories(c).includes(activeTab));

  return (
    <div className={`conversations-page${lang === 'ar' ? ' rtl' : ''}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>

      {/* ── Left panel: list ── */}
      <div
        className={`conv-list-panel${mobileView === 'map' || mobileView === 'detail' ? ' mobile-hidden' : ''}`}
        style={{ width: panelWidth, minWidth: panelWidth }}
      >

        {/* Panel header */}
        <div className="conv-panel-header">
          <div className="conv-panel-title-row">
            <h2 className="conv-panel-title">{t.conversationsTitle}</h2>
            <button
              className="show-map-btn"
              onClick={() => setMobileView(mobileView === 'map' ? 'list' : 'map')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
              {t.showOnMap}
            </button>
          </div>

          {/* Category tabs */}
          <div className="conv-tabs" role="tablist">
            {CATEGORIES.map(cat => {
              const label = t[cat.labelKey] as string;
              const count = counts.get(cat.key) ?? 0;
              return (
                <button
                  key={cat.key}
                  role="tab"
                  aria-selected={activeTab === cat.key}
                  className={`conv-tab${activeTab === cat.key ? ' active' : ''}`}
                  onClick={() => setActiveTab(cat.key)}
                >
                  {label} {count}
                </button>
              );
            })}
          </div>

          {/* Search + reset + filter */}
          <div className="conv-search-row">
            <input
              className="conv-search-input"
              type="text"
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button
              className="conv-reset-btn"
              onClick={() => { setSearch(''); }}
            >
              {t.resetFilter}
            </button>
            <button className="conv-filter-btn" title={t.filterBtn}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Card list */}
        <div className="conv-cards-list">
          {loading ? (
            <div className="conv-placeholder">{t.loading}</div>
          ) : tabFiltered.length === 0 ? (
            <div className="conv-placeholder">{t.noConversations}</div>
          ) : (
            tabFiltered.map(conv => {
              const badges = getStatusBadges(conv);
              const name = getDisplayName(conv);
              return (
                <div
                  key={conv.id}
                  className={`conv-card${selectedId === conv.id ? ' selected' : ''}`}
                  onClick={() => openModal(conv)}
                >
                  {/* ── Card body: name, location, phone ── */}
                  <div className="conv-card-body">
                    <div className="conv-card-top">
                      <span className="conv-card-name">{name}</span>
                      <span className="conv-card-date">{formatDate(conv.created_at)}</span>
                    </div>

                    {(conv.location_address || conv.location_lat) && (
                      <div className="conv-card-row">
                        <svg className="conv-card-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                        <span className="conv-card-addr">
                          {conv.location_address || `${conv.location_lat?.toFixed(4)}, ${conv.location_lon?.toFixed(4)}`}
                        </span>
                      </div>
                    )}

                    {conv.user_phone && (
                      <div className="conv-card-row">
                        <svg className="conv-card-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.39 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.16 6.16l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                        <span className="conv-card-phone">{conv.user_phone}</span>
                      </div>
                    )}
                  </div>

                  {/* ── Separator line ── */}
                  <div className="conv-card-divider" />

                  {/* ── Badge row: INC left, status+agency right ── */}
                  <div className="conv-card-badges">
                    {conv.incident_number ? (
                      <span className="conv-card-incident">{conv.incident_number}</span>
                    ) : (
                      <span />
                    )}
                    <div className="conv-card-badge-right">
                      {badges.map(b => (
                        <span key={b.label} className={`conv-card-badge badge--${b.type}`}>{b.label}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── Drag-resize handle on right edge ── */}
        <div className="panel-resize-handle" onMouseDown={onResizeMouseDown} />
      </div>

      {/* ── Inline detail panel (opens beside the list panel) ── */}
      {loadingModal && (
        <div className="conv-detail-panel" style={{ width: detailWidth, minWidth: detailWidth }}>
          <div className="conv-detail-loading">
            <div className="modal-spinner" />
          </div>
          <div className="panel-resize-handle detail-resize-handle" onMouseDown={onDetailResizeMouseDown} />
        </div>
      )}
      {!loadingModal && modalConv && (
        <div className="conv-detail-panel" style={{ width: detailWidth, minWidth: detailWidth }}>
          <ConversationModal
            conversation={modalConv}
            onClose={closeModal}
            onResolve={handleResolve}
          />
          <div className="panel-resize-handle detail-resize-handle" onMouseDown={onDetailResizeMouseDown} />
        </div>
      )}

      {/* ── Right panel: map (desktop + mobile map view) ── */}
      <div className={`conv-map-panel${mobileView === 'list' ? ' mobile-hidden' : ''}${mobileView === 'detail' ? ' mobile-hidden' : ''}`}>
        {mobileView === 'map' && (
          <button className="map-back-btn" onClick={() => setMobileView('list')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            {lang === 'ar' ? 'رجوع' : 'Back'}
          </button>
        )}
        <MapView
          conversations={searched}
          selectedId={selectedId}
          onSelectConversation={openModal}
          onResolve={handleResolve}
        />
      </div>

      {/* ── Mobile detail view: map (top) + chat (bottom) ── */}
      {isMobile && mobileView === 'detail' && (
        <div className="mobile-detail-overlay">
          {/* Map section – top 38% */}
          <div className="mobile-detail-map">
            <button className="mobile-back-btn" onClick={closeModal}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              Back
            </button>
            <MapView
              conversations={selectedId ? searched.filter(c => c.id === selectedId) : []}
              selectedId={selectedId}
              onSelectConversation={openModal}
              onResolve={handleResolve}
            />
          </div>

          {/* Chat section – bottom 62% */}
          <div className="mobile-detail-chat">
            {loadingModal ? (
              <div className="conv-detail-loading"><div className="modal-spinner" /></div>
            ) : modalConv ? (
              <ConversationModal
                conversation={modalConv}
                onClose={closeModal}
                onResolve={handleResolve}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
